import React, { useState, useEffect } from 'react';
import { User, Tenant } from '../types/user';
import { UserWithMetrics, TenantStats, userManagementService } from '../services/user-management-service';
import { InviteUserModal } from './InviteUserModal';
import { EditUserModal } from './EditUserModal';
import PlansModal from './PlansModal';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, UserPlus, Users, MoreVertical, Shield, User as UserIcon, Clock, Activity, Trash2, Edit } from 'lucide-react';

interface UserManagementScreenProps {
  user: User;
  tenant: Tenant;
  onBack: () => void;
}

export function UserManagementScreen({ user, tenant, onBack }: UserManagementScreenProps) {
  const [users, setUsers] = useState<UserWithMetrics[]>([]);
  const [stats, setStats] = useState<TenantStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserWithMetrics | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    loadData();
  }, [tenant.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersData, statsData] = await Promise.all([
        userManagementService.getTenantUsers(tenant.id),
        userManagementService.getTenantStats(tenant.id)
      ]);
      setUsers(usersData);
      setStats(statsData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = () => {
    if (!stats?.canAddUsers) {
      setShowUpsellModal(true);
      return;
    }
    setShowInviteModal(true);
  };

  const handleEditUser = (userToEdit: UserWithMetrics) => {
    setSelectedUser(userToEdit);
    setShowEditModal(true);
    setOpenMenuId(null);
  };

  const handleUserInvited = async () => {
    setShowInviteModal(false);
    toast.success('Usuário convidado com sucesso!');
    await loadData();
  };

  const handleUserUpdated = async () => {
    setShowEditModal(false);
    setSelectedUser(null);
    toast.success('Usuário atualizado com sucesso!');
    await loadData();
  };

  const handleDeleteUser = async (userToDelete: UserWithMetrics) => {
    if (userToDelete.role === 'owner') {
      toast.error('Não é possível remover o proprietário da conta');
      return;
    }

    if (!confirm(`Tem certeza que deseja remover ${userToDelete.name} do tenant?`)) {
      return;
    }

    try {
      const result = await userManagementService.deleteUser(userToDelete.id);
      
      if (!result.success) {
        toast.error(result.error || 'Erro ao remover usuário');
        return;
      }

      toast.success('Usuário removido com sucesso!');
      setOpenMenuId(null);
      await loadData();
    } catch (error) {
      console.error('Erro ao remover usuário:', error);
      toast.error('Erro ao remover usuário');
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-100 text-purple-800';
      case 'manager':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner':
        return 'Proprietário';
      case 'manager':
        return 'Administrador';
      default:
        return 'Usuário';
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando usuários...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Gestão de Usuários</h1>
              <p className="text-sm text-gray-600">Gerencie os usuários da sua empresa</p>
            </div>
          </div>
          <button
            onClick={handleInviteUser}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <UserPlus className="w-5 h-5" />
            <span>Convidar Usuário</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="px-6 py-4 bg-white border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Card: Usuários Ativos */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600">Usuários Ativos</p>
                  <p className="text-3xl font-bold text-blue-900 mt-1">
                    {stats.currentUsers} / {stats.maxUsers === 999999 ? '∞' : stats.maxUsers}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-200 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-700" />
                </div>
              </div>
            </div>

            {/* Card: Plano Atual */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600">Plano Atual</p>
                  <p className="text-2xl font-bold text-purple-900 mt-1 capitalize">
                    {stats.plan === 'free' ? 'Gratuito' : 'Premium'}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-200 rounded-lg flex items-center justify-center">
                  <Shield className="w-6 h-6 text-purple-700" />
                </div>
              </div>
            </div>

            {/* Card: Total de Interações */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600">Interações (Mês)</p>
                  <p className="text-3xl font-bold text-green-900 mt-1">
                    {users.reduce((sum, u) => sum + u.monthlyInteractions, 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-200 rounded-lg flex items-center justify-center">
                  <Activity className="w-6 h-6 text-green-700" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="flex-1 overflow-auto p-6">
        <div className="bg-white rounded-lg shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usuário
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Interações (Mês)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Último Acesso
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((userData) => (
                <tr key={userData.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-medium flex-shrink-0">
                        {userData.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">{userData.name}</div>
                        {userData.status === 'inactive' && (
                          <div className="text-xs text-red-600">Inativo</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{userData.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(userData.role)}`}>
                      {getRoleLabel(userData.role)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-900">
                      <Activity className="w-4 h-4 text-gray-400 mr-2" />
                      {userData.monthlyInteractions}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-500">
                      <Clock className="w-4 h-4 text-gray-400 mr-2" />
                      {formatDate(userData.lastActivity)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium relative">
                    {userData.role !== 'owner' && (
                      <div>
                        <button
                          onClick={() => setOpenMenuId(openMenuId === userData.id ? null : userData.id)}
                          className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
                        >
                          <MoreVertical className="w-5 h-5" />
                        </button>

                        {openMenuId === userData.id && (
                          <>
                            {/* Backdrop */}
                            <div 
                              className="fixed inset-0 z-10" 
                              onClick={() => setOpenMenuId(null)}
                            />
                            
                            {/* Dropdown Menu */}
                            <div 
                              className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20"
                              style={{ zIndex: 9999 }}
                            >
                              <div className="py-1">
                                <button
                                  onClick={() => handleEditUser(userData)}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                                >
                                  <Edit className="w-4 h-4" />
                                  <span>Editar</span>
                                </button>
                                
                                {userData.role !== 'owner' && (
                                  <button
                                    onClick={() => handleDeleteUser(userData)}
                                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    <span>Remover do Tenant</span>
                                  </button>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {users.length === 0 && (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">Nenhum usuário encontrado</p>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showInviteModal && (
        <InviteUserModal
          tenant={tenant}
          currentUser={user}
          onClose={() => setShowInviteModal(false)}
          onSuccess={handleUserInvited}
        />
      )}

      {showEditModal && selectedUser && (
        <EditUserModal
          user={selectedUser}
          currentUserRole={user.role}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onSuccess={handleUserUpdated}
        />
      )}

      {showUpsellModal && (
        <PlansModal
          isOpen={showUpsellModal}
          onClose={() => setShowUpsellModal(false)}
          currentPlan={tenant.plan}
        />
      )}
    </div>
  );
}

